import useProfile from "@/hooks/useProfile";
import useUpdateMember from "@/hooks/useUpdateMember";
import { useLocaleShallow } from "@/stores/locale";
import { useEffect, useRef } from "react";

/**
 * Reconciles the local language preference with the backend profile, mounted
 * once on the authenticated dashboard shell:
 *  - If the server already has a saved locale and the user hasn't explicitly
 *    chosen one on this device, adopt the server's value (so a language picked
 *    on one device follows the user to another).
 *  - If the server has no saved locale yet (new/pre-existing account), write
 *    the detected/current local value once - this is what makes outbound
 *    emails and push notifications follow the user's language.
 * A ref guards the write so it fires at most once per mount rather than on
 * every profile refetch.
 */
function useLocaleSync() {
	const { profile } = useProfile();
	const { locale, adoptFromProfile } = useLocaleShallow((state) => ({
		locale: state.locale,
		adoptFromProfile: state.adoptFromProfile,
	}));
	const updateMember = useUpdateMember();
	const hasSynced = useRef(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally scoped to [profile?.id, profile?.locale] - adoptFromProfile/updateMember.mutate are stable references, and `locale` is read fresh rather than tracked so a later local change doesn't re-fire this initial-sync effect.
	useEffect(() => {
		if (!profile?.id || hasSynced.current) return;

		if (profile.locale) {
			adoptFromProfile(profile.locale);
			hasSynced.current = true;
			return;
		}

		hasSynced.current = true;
		updateMember.mutate({ id: profile.id, locale });
	}, [profile?.id, profile?.locale]);

	// If the user changes the language locally after the initial sync, push it
	// to the server too, so future emails/push follow the new choice.
	const previousLocale = useRef(locale);
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally scoped to [locale, profile?.id] - profile.locale is only read inside the ref-guarded branch above, and updateMember.mutate is a stable mutation reference.
	useEffect(() => {
		if (!profile?.id || !hasSynced.current) return;
		if (previousLocale.current === locale) return;
		previousLocale.current = locale;
		if (profile.locale !== locale) {
			updateMember.mutate({ id: profile.id, locale });
		}
	}, [locale, profile?.id]);
}

export default useLocaleSync;
